import React, { useEffect, useRef, useState, useCallback } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  FaLayerGroup,
  FaMapMarkerAlt,
  FaWifi,
  FaMousePointer
} from 'react-icons/fa';
import styles from '../../styles/Cadastral.module.css';

const MAP_LAYERS = {
  streets: {
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attr: '&copy; colaboradores de OpenStreetMap',
    max: 19
  },
  satellite: {
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attr: 'Tiles &copy; Esri, Maxar, Earthstar Geographics',
    max: 19
  },
  terrain: {
    url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
    attr: '&copy; OpenStreetMap · SRTM | OpenTopoMap',
    max: 17
  }
};

/* Offline SVG fallback. */
function DrawMapFallback({ record, caseFile, showPolygon, onOpenRecord }) {
  if (!record) return null;
  const W = 800;
  const H = 600;
  const cx = W / 2;
  const cy = H / 2;

  let seed = 0;
  for (let i = 0; i < record.propertyKey.length; i++) {
    seed = (seed * 31 + record.propertyKey.charCodeAt(i)) % 100000;
  }
  const rnd = () => {
    seed = (seed * 1103515245 + 12345) % 2147483648;
    return seed / 2147483648;
  };

  const cityBlocks = [];
  for (let gx = 0; gx < 6; gx++) {
    for (let gy = 0; gy < 5; gy++) {
      const x = 30 + gx * 128 + rnd() * 12;
      const y = 26 + gy * 118 + rnd() * 10;
      const w = 88 + rnd() * 26;
      const h = 76 + rnd() * 22;
      cityBlocks.push(
        <rect
          key={`${gx}-${gy}`}
          x={x.toFixed(1)}
          y={y.toFixed(1)}
          width={w.toFixed(1)}
          height={h.toFixed(1)}
          rx="3"
          fill="#dfe6f1"
          stroke="#c8d2e4"
          strokeWidth="1"
        />
      );
    }
  }

  const streetLines = [];
  for (let i = 1; i < 6; i++) {
    streetLines.push(
      <line
        key={`v-${i}`}
        x1={i * 128 + 12}
        y1={0}
        x2={i * 128 + 12}
        y2={H}
        stroke="#fff"
        strokeWidth="9"
      />
    );
  }
  for (let i = 1; i < 5; i++) {
    streetLines.push(
      <line
        key={`h-${i}`}
        x1={0}
        y1={i * 118 + 10}
        x2={W}
        y2={i * 118 + 10}
        stroke="#fff"
        strokeWidth="9"
      />
    );
  }

  const polygonSide = Math.min(
    150,
    Math.max(54, Math.sqrt(caseFile?.land?.area || 200) * 2.6)
  );

  return (
    <div className={`${styles.mapFallback} ${styles.isShown}`}>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid slice" style={{ width: '100%', height: '100%' }}>
        <rect width={W} height={H} fill="#eaf0f7" />
        {cityBlocks}
        {streetLines}
        {showPolygon && (
          <rect
            x={cx - polygonSide / 2}
            y={cy - polygonSide / 2}
            width={polygonSide}
            height={polygonSide}
            rx="2"
            fill="rgba(236,72,153,.15)"
            stroke="#ec4899"
            strokeWidth="2.5"
            strokeDasharray="6 4"
          />
        )}
        <g style={{ cursor: 'pointer' }} onClick={onOpenRecord}>
          <circle cx={cx} cy={cy} r="26" fill="rgba(49,69,156,.18)">
            <animate attributeName="r" values="14;30;14" dur="2s" repeatCount="indefinite" />
            <animate attributeName="opacity" values=".55;0;.55" dur="2s" repeatCount="indefinite" />
          </circle>
          <circle cx={cx} cy={cy} r="10" fill="#31459c" stroke="#fff" strokeWidth="3.5" />
        </g>
        <text
          x={cx}
          y={cy + polygonSide / 2 + 22}
          textAnchor="middle"
          fontSize="12"
          fill="#5b6577"
          fontFamily="system-ui,sans-serif"
        >
          {record.address || 'Predio'}
        </text>
      </svg>
    </div>
  );
}

export default function CadastralMap({
  currentProperty,
  isDetail,
  onOpenRecord
}) {
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const tileLayerRef = useRef(null);
  const markerRef = useRef(null);
  const polygonRef = useRef(null);

  const [activeLayer, setActiveLayer] = useState('streets');
  const [showPolygon, setShowPolygon] = useState(true);
  const [offline, setOffline] = useState(false);
  const [showHint, setShowHint] = useState(false);

  /* Initialize the map. */
  useEffect(() => {
    if (!mapContainerRef.current) return;

    if (!mapInstanceRef.current) {
      const map = L.map(mapContainerRef.current, {
        zoomControl: false,
        attributionControl: true,
        scrollWheelZoom: true
      }).setView([20.5888, -100.3899], 12); // Santiago de Querétaro

      L.control.zoom({ position: 'bottomright' }).addTo(map);
      mapInstanceRef.current = map;

      // Initial tile layer.
      const initialTile = L.tileLayer(MAP_LAYERS.streets.url, {
        maxZoom: MAP_LAYERS.streets.max,
        attribution: MAP_LAYERS.streets.attr
      }).addTo(map);

      tileLayerRef.current = initialTile;

      let tilesLoaded = 0;
      initialTile.on('tileload', () => {
        tilesLoaded++;
      });
      const timer = setTimeout(() => {
        if (tilesLoaded === 0) {
          setOffline(true);
        }
      }, 5000);

      return () => {
        clearTimeout(timer);
        map.remove();
        mapInstanceRef.current = null;
      };
    }
  }, []);

  /* Change the map layer. */
  const changeLayer = useCallback((layerId) => {
    const map = mapInstanceRef.current;
    if (!map) return;
    const layerConfig = MAP_LAYERS[layerId];
    if (tileLayerRef.current) {
      map.removeLayer(tileLayerRef.current);
    }
    const newTileLayer = L.tileLayer(layerConfig.url, {
      maxZoom: layerConfig.max,
      attribution: layerConfig.attr
    }).addTo(map);
    tileLayerRef.current = newTileLayer;
    setActiveLayer(layerId);
  }, []);

  /* Invalidate map size when its width changes, such as when opening the record panel. */
  useEffect(() => {
    if (!mapInstanceRef.current) return;
    const interval = setInterval(() => {
      mapInstanceRef.current?.invalidateSize();
    }, 60);
    const timeout = setTimeout(() => clearInterval(interval), 600);
    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [isDetail]);

  /* Update the map property. */
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    // Remove previous layers.
    if (markerRef.current) {
      map.removeLayer(markerRef.current);
      markerRef.current = null;
    }
    if (polygonRef.current) {
      map.removeLayer(polygonRef.current);
      polygonRef.current = null;
    }

    if (!currentProperty || !currentProperty.record) {
      setShowHint(false);
      return;
    }

    const { record, file } = currentProperty;

    if (!record.latitude || !record.longitude) {
      setShowHint(false);
      return;
    }

    // Property polygon.
    if (file?.polygon) {
      const poly = L.polygon(file.polygon, {
        color: '#ec4899',
        weight: 2.5,
        fillColor: '#ec4899',
        fillOpacity: 0.14,
        dashArray: '6 4'
      });
      polygonRef.current = poly;
      if (showPolygon) {
        poly.addTo(map);
      }
    }

    // Custom pulsing marker.
    const pinIcon = L.divIcon({
      className: '',
      iconSize: [34, 34],
      iconAnchor: [17, 17],
      html: '<div class="pin-wrap"><div class="pin-pulse"></div><div class="pin-dot" title="Ver ficha del predio"></div></div>'
    });

    const marker = L.marker([record.latitude, record.longitude], { icon: pinIcon }).addTo(map);
    marker.on('click', () => {
      onOpenRecord();
    });
    markerRef.current = marker;

    setShowHint(!isDetail);

    // Fly to the location.
    try {
      map.flyTo([record.latitude, record.longitude], 18, { duration: 1.1 });
    } catch {
      map.setView([record.latitude, record.longitude], 18);
    }
  }, [currentProperty, isDetail, onOpenRecord, showPolygon]);

  /* Control polygon visibility. */
  const togglePolygonVisibility = () => {
    setShowPolygon(prev => {
      const next = !prev;
      const map = mapInstanceRef.current;
      if (polygonRef.current && map) {
        if (next) polygonRef.current.addTo(map);
        else map.removeLayer(polygonRef.current);
      }
      return next;
    });
  };

  /* Center on the property. */
  const handleCenterOnProperty = () => {
    const map = mapInstanceRef.current;
    if (!map || !currentProperty?.record?.latitude) return;
    map.flyTo([currentProperty.record.latitude, currentProperty.record.longitude], 18, { duration: 0.8 });
  };

  const missingGeoreference = currentProperty?.record && (!currentProperty.record.latitude || !currentProperty.record.longitude);

  return (
    <div className={styles.mapStage} id="mapStage">
      {/* Leaflet container. */}
      <div
        ref={mapContainerRef}
        id="map"
        className={styles.leafletContainer}
        style={{ display: offline || missingGeoreference ? 'none' : 'block' }}
      />

      {/* Local cartography fallback when offline or missing georeference. */}
      {offline && currentProperty && (
        <DrawMapFallback
          record={currentProperty.record}
          caseFile={currentProperty.file}
          showPolygon={showPolygon}
          onOpenRecord={onOpenRecord}
        />
      )}

      {missingGeoreference && (
        <div className={styles.noGeoFallback}>
          <div className={styles.noGeoCard}>
            <div className={`${styles.stateIco} ${styles.warn}`}>
              <FaMapMarkerAlt size={28} />
            </div>
            <h3>Predio sin georreferencia</h3>
            <p>
              El padrón catastral no cuenta con coordenadas registradas para esta clave.
              La información alfanumérica está disponible en el expediente.
            </p>
          </div>
        </div>
      )}

      {offline && (
        <div className={styles.mapOfflineNote}>
          <FaWifi size={13} />
          <span>Cartografía local — sin conexión con los servidores de mapas</span>
        </div>
      )}

      {/* Floating map controls. */}
      {!offline && !missingGeoreference && (
        <div className={styles.mapTools}>
          <div className={styles.mapLayers}>
            <button
              type="button"
              className={activeLayer === 'streets' ? styles.layerActive : ''}
              onClick={() => changeLayer('streets')}
            >
              Calles
            </button>
            <button
              type="button"
              className={activeLayer === 'satellite' ? styles.layerActive : ''}
              onClick={() => changeLayer('satellite')}
            >
              Satélite
            </button>
            <button
              type="button"
              className={activeLayer === 'terrain' ? styles.layerActive : ''}
              onClick={() => changeLayer('terrain')}
            >
              Relieve
            </button>
          </div>

          <div className={styles.mapToolsRow}>
            <button
              type="button"
              className={`${styles.iconBtn} ${showPolygon ? styles.isOn : ''}`}
              onClick={togglePolygonVisibility}
              title="Mostrar u ocultar el polígono catastral"
            >
              <FaLayerGroup size={15} />
            </button>
            <button
              type="button"
              className={styles.iconBtn}
              onClick={handleCenterOnProperty}
              title="Centrar en el predio"
              disabled={!currentProperty?.record?.latitude}
            >
              <FaMapMarkerAlt size={15} />
            </button>
          </div>
        </div>
      )}

      {showHint && !isDetail && (
        <div className={styles.mapHint} onClick={onOpenRecord}>
          <span className={styles.mhIco}><FaMousePointer size={13} /></span>
          <span>Haz clic en el punto para ver el expediente del predio</span>
        </div>
      )}

      {currentProperty?.record?.latitude && !missingGeoreference && (
        <div className={styles.coordsBadge}>
          {currentProperty.record.latitude.toFixed(6)}, {currentProperty.record.longitude.toFixed(6)}
        </div>
      )}
    </div>
  );
}
